import {
  bookingTrackingLocalStorageKey,
  getBookingTrackingSessionId,
} from '@/components/consultation/bookingTracking/bookingTrackingSession'
import {
  type BookingTrackingEvent,
  type BookingTrackingPersistedBuffer,
} from '@/components/consultation/bookingTracking/bookingTrackingTypes'

function emptyBuffer(): BookingTrackingPersistedBuffer {
  return { events: {} }
}

/** Source of truth when sessionStorage writes fail (quota, disabled storage, etc.). */
const memoryBuffers = new Map<string, BookingTrackingPersistedBuffer>()

function readBufferFromSessionStorage(sessionId: string): BookingTrackingPersistedBuffer | null {
  if (typeof sessionStorage === 'undefined') {
    return null
  }
  try {
    const raw = sessionStorage.getItem(bookingTrackingLocalStorageKey(sessionId))
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as BookingTrackingPersistedBuffer
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !parsed.events ||
      typeof parsed.events !== 'object'
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function readBookingTrackingBuffer(
  sessionId = getBookingTrackingSessionId(),
): BookingTrackingPersistedBuffer {
  const memoryBuffer = memoryBuffers.get(sessionId)
  if (memoryBuffer) {
    return memoryBuffer
  }
  return readBufferFromSessionStorage(sessionId) ?? emptyBuffer()
}

export function writeBookingTrackingBuffer(
  buffer: BookingTrackingPersistedBuffer,
  sessionId = getBookingTrackingSessionId(),
): void {
  memoryBuffers.set(sessionId, buffer)
  if (typeof sessionStorage === 'undefined') {
    return
  }
  try {
    sessionStorage.setItem(bookingTrackingLocalStorageKey(sessionId), JSON.stringify(buffer))
  } catch {
    // Quota / private mode — tracking must not break booking; memoryBuffers retains events.
  }
}

/** @internal test hook */
export function resetBookingTrackingStorageForTests(): void {
  memoryBuffers.clear()
}

export function allocateBookingTrackingTimestampKey(
  existingKeys: Iterable<string>,
  nowMs = Date.now(),
): string {
  const keySet = new Set(existingKeys)
  const base = String(nowMs)
  if (!keySet.has(base)) {
    return base
  }
  let suffix = 1
  while (keySet.has(`${base}_${suffix}`)) {
    suffix += 1
  }
  return `${base}_${suffix}`
}

export function appendBookingTrackingEvent(event: BookingTrackingEvent): string {
  const sessionId = getBookingTrackingSessionId()
  const buffer = readBookingTrackingBuffer(sessionId)
  const key = allocateBookingTrackingTimestampKey(Object.keys(buffer.events))
  buffer.events[key] = {
    ...event,
    sessionId: event.sessionId ?? sessionId,
  }
  writeBookingTrackingBuffer(buffer, sessionId)
  return key
}

/** Tag buffer-only events once this tab's consultation id is known. */
export function tagUntaggedBookingTrackingEvents(
  consultationId: string,
  sessionId = getBookingTrackingSessionId(),
): void {
  const buffer = readBookingTrackingBuffer(sessionId)
  let changed = false
  for (const event of Object.values(buffer.events)) {
    if (event && !event.consultationId) {
      event.consultationId = consultationId
      changed = true
    }
  }
  if (changed) {
    writeBookingTrackingBuffer(buffer, sessionId)
  }
}

export function listPendingBookingTrackingKeys(
  consultationId?: string | null,
  sessionId = getBookingTrackingSessionId(),
): string[] {
  const buffer = readBookingTrackingBuffer(sessionId)
  return Object.keys(buffer.events).filter((key) => {
    const event = buffer.events[key]
    if (!event) {
      return false
    }
    if (!consultationId) {
      return true
    }
    return !event.consultationId || event.consultationId === consultationId
  })
}

export function removeBookingTrackingBufferKeys(
  keys: string[],
  sessionId = getBookingTrackingSessionId(),
): void {
  if (keys.length === 0) {
    return
  }
  const buffer = readBookingTrackingBuffer(sessionId)
  for (const key of keys) {
    delete buffer.events[key]
  }
  writeBookingTrackingBuffer(buffer, sessionId)
}
